import { FC, ReactElement, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

import { DialogMenu, SelectBox } from "@/components";
import {
  filter_black_icon,
  userManagementStatus,
  userRightOptions,
} from "@/constants";

export interface IFilter {
  onFiltersChange: (val: UserFilter) => Promise<void>;
  onClose: () => void;
  anchorEl: Element | null;
  initialFilterState: UserFilter;
}

const Filters: FC<IFilter> = ({
  onFiltersChange,
  onClose,
  anchorEl,
  initialFilterState,
}): ReactElement => {
  const [filters, setFilters] = useState(initialFilterState);
  const [filterLoading, setFilterLoading] = useState(false);
  const applyFilters = async (): Promise<void> => {
    setFilterLoading(true);
    try {
      await onFiltersChange(filters);
    } catch (error) {
    } finally {
      setFilterLoading(false);
      onClose();
    }
  };

  const resetFilters = (): void => {
    setFilters(initialFilterState);
    onFiltersChange(initialFilterState);
    onClose();
  };

  const handleChangeValue =
    (name: keyof UserFilter) =>
    (value: string): void => {
      setFilters({
        ...filters,
        [name]: value,
      });
    };

  return (
    <DialogMenu
      id="advance-filter-menu"
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        p={2.5}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <img src={filter_black_icon} alt="filter" />
          <Typography
            variant="h5"
            fontSize={18}
            fontWeight={600}
            color="#030712"
          >
            Filter
          </Typography>
          <Box
            sx={{
              width: 24,
              height: 24,
              display: "grid",
              placeContent: "center",
              backgroundColor: "error.main",
              color: "white",
              borderRadius: "50%",
            }}
          >
            <Typography variant="caption" fontWeight={500} color="white">
              {Object.values(filters).filter(Boolean).length}
            </Typography>
          </Box>
        </Stack>

        <Typography
          sx={{
            color: "#116B6F",
            textDecorationLine: "underline",
            cursor: "pointer",
          }}
          variant="body2"
          fontWeight={500}
          onClick={resetFilters}
        >
          Clear All
        </Typography>
      </Stack>
      <Divider />
      <Stack sx={{ p: 2 }} spacing={1}>
        <SelectBox
          label="Status"
          placeholder="Select Status"
          value={filters.status}
          handleChangeValue={handleChangeValue("status")}
          options={userManagementStatus}
        />
        <SelectBox
          label="Role"
          placeholder="Select Role"
          value={filters.userLevel}
          handleChangeValue={handleChangeValue("userLevel")}
          options={userRightOptions}
        />
      </Stack>
      <Divider />
      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={1}
        p={2}
        sx={{
          background: "#F7F8F8",
        }}
      >
        <Button variant="white-outlined" sx={{ flex: 1 }} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ flex: 1, gap: 1 }}
          onClick={applyFilters}
          disabled={filterLoading}
          startIcon={filterLoading ? <CircularProgress size={20} /> : null}
        >
          {filterLoading ? "Applying" : "Apply"}
        </Button>
      </Stack>
    </DialogMenu>
  );
};

export default Filters;
